import { createUseStyles } from "react-jss";
import type { Boat, BookingPolicy } from "../../../../common/src";
import { DAMAGE_POLICY_LABELS } from "../../../../common/src";
import { boatPhotoUrl } from "../../../../common/src";
import type { VetBoatsTheme } from "../../theme";
import { Card } from "../shared/Card";

const useStyles = createUseStyles((theme: VetBoatsTheme) => ({
  facts: {
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    gap: "4px 12px",
    margin: "0 0 14px",
    "& dt": { color: theme.color.muted },
    "& dd": { margin: 0 },
  },
  rules: { whiteSpace: "pre-line", margin: 0, lineHeight: 1.5 },
  heading: {
    fontSize: 14,
    margin: "14px 0 6px",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: theme.color.muted,
  },
  list: { margin: 0, paddingLeft: 18, lineHeight: 1.6 },
  description: { marginTop: 0 },
  photo: {
    display: "block",
    width: "100%",
    aspectRatio: "3 / 2",
    objectFit: "cover",
    borderRadius: theme.radius,
    marginBottom: 14,
    background: theme.color.background,
  },
}));

const days = (n: number): string => `${n} day${n === 1 ? "" : "s"}`;

interface BoatSummaryProps {
  boat: Boat;
  policy?: BookingPolicy;
}

export const BoatSummary = ({ boat, policy }: BoatSummaryProps) => {
  const classes = useStyles();
  return (
    <Card title={boat.name}>
      {boat.photoUpdatedAt && (
        <img
          className={classes.photo}
          src={boatPhotoUrl(boat.id, boat.photoUpdatedAt)}
          alt={`Photo of ${boat.name}`}
          loading="lazy"
        />
      )}
      {boat.description && (
        <p className={classes.description}>{boat.description}</p>
      )}
      <dl className={classes.facts}>
        {boat.makeModel && (
          <>
            <dt>Boat</dt>
            <dd>
              {boat.makeModel}
              {boat.lengthFeet ? `, ${boat.lengthFeet} ft` : ""}
            </dd>
          </>
        )}
        {boat.storageLocation && (
          <>
            <dt>Storage location</dt>
            <dd>{boat.storageLocation}</dd>
          </>
        )}
        <dt>Capacity</dt>
        <dd>{boat.capacity} people</dd>
        <dt>Owner</dt>
        <dd>{boat.ownerName}</dd>
        <dt>Boat damage</dt>
        <dd>{DAMAGE_POLICY_LABELS[boat.damagePolicy]}</dd>
      </dl>
      <h3 className={classes.heading}>Booking limits</h3>
      <ul className={classes.list}>
        <li>Up to {days(boat.maxConsecutiveDays)} in a row</li>
        <li>
          {boat.minAdvanceDays === 0
            ? "Same-day bookings allowed"
            : `Book at least ${days(boat.minAdvanceDays)} ahead`}
        </li>
        <li>Book up to {days(boat.maxAdvanceDays)} in advance</li>
        {policy && (
          <li>
            {policy.maxUpcomingReservationsPerPerson} upcoming reservation
            {policy.maxUpcomingReservationsPerPerson === 1 ? "" : "s"} per
            person (across all boats)
          </li>
        )}
      </ul>
      {boat.rules && (
        <>
          <h3 className={classes.heading}>Rules</h3>
          <p className={classes.rules}>{boat.rules}</p>
        </>
      )}
    </Card>
  );
};
